export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

type QueryValue = string | number | boolean | null | undefined;

export function toSearchParams(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  form?: FormData;
  query?: Record<string, QueryValue>;
}

/** Same-origin call to the Lucky Try API; the session cookie travels automatically. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(`/api${path}${options.query ? toSearchParams(options.query) : ""}`, {
    method: options.method ?? (options.body !== undefined || options.form ? "POST" : "GET"),
    credentials: "same-origin",
    headers: options.body !== undefined ? { "content-type": "application/json" } : undefined,
    body: options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
  });
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    const error = (
      payload as { error?: { code?: string; message?: string; details?: unknown } } | null
    )?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? "http_error",
      error?.message ?? response.statusText,
      error?.details,
    );
  }
  return payload as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === "validation_failed" && Array.isArray(error.details)) {
      const first = error.details[0] as { path?: string; message?: string } | undefined;
      return first ? `${first.path ? `${first.path}: ` : ""}${first.message}` : error.message;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong";
}
